import React from 'react';
import clsx from 'clsx';
import {
  Form,
  Navigate,
  useLoaderData,
  useNavigate,
  useNavigation,
  useSubmit,
  useRevalidator,
  useActionData,
  useOutlet,
  useLocation
} from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';

import * as Headless from '@headlessui/react';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  CubeIcon,
  DocumentIcon,
  EyeIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Spinner } from '@oysterjs/ui/spinner';
import { PageTitle } from '@oysterjs/ui/page';
import { DataTable } from '@oysterjs/ui/data-table';
import { Input } from '@oysterjs/ui/input';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Switch, SwitchField } from '@oysterjs/ui/switch';
import { SearchFilterInput, useFiltersFromLocalStorage } from '@oysterjs/ui/search-filter';
import {
  getPresetColumns,
  presetSortFn,
  type PresetSortKey
} from '~/components/presets/presets-columns';
import {
  createPresetFilterDefinitions,
  filterPresets,
  type PresetFilter
} from '~/api/data/presets/presets-filters';

import { client } from '~/api/client';
import {
  FederatedChildAgencyFieldsFragment,
  Action,
  CaraWorkspaceEffortLevel,
  CaraWorkspacePreset,
  CreateCaraWorkspaceDocument,
  CaraWorkspaceFileInput,
  CreateCaraWorkspaceMutation,
  CreateCustomPresetMutation,
  CustomFormListFieldsFragment,
  CustomPresetDetailFieldsFragment,
  CustomPresetFieldsFragment,
  CustomPresetInput,
  HtmlDocumentTemplateFieldsFragment,
  Resource,
  UpdateCustomPresetMutation,
  UpdateCustomPresetsDocument,
  VisibilityConfigInput,
  WordDocumentTemplateFieldsFragment,
  ExcelSpreadsheetTemplateFieldsFragment
} from '~/api/graphql';
import { type TemplatePickerOption } from '~/components/cara-input/cara-input-template-picker-dialog';
import { OutputTemplatePicker } from '~/components/cara-input/output-template-picker';
import { useCaraAttachments } from '~/components/cara-input/use-cara-attachments';
import {
  customFormPickerOptions,
  excelTemplatePickerOptions,
  htmlTemplatePickerOptions,
  wordTemplatePickerOptions
} from '~/components/cara-input/template-picker-options';
import {
  presetCustomFormPickerDescription,
  presetExcelTemplatePickerDescription,
  presetHTMLTemplatePickerDescription,
  presetWordTemplatePickerDescription
} from '~/components/cara-input/preset-picker-descriptions';
import {
  visibilityInputFromValue,
  type VisibilitySelectorValue
} from '~/components/visibility/visibility-selector.utils';
import { VisibilitySplitButton } from '~/components/visibility/visibility-popover';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';
import { useVisibilityEditAllowed } from '~/components/visibility/use-visibility-edit-allowed';
import {
  defaultVisibilityValueFromFragment,
  useDefaultVisibilityValue
} from '~/components/visibility/default-visibility';
import { useCurrentAgencyUser } from '~/components/current-user';
import {
  CREATE_CARA_WORKSPACE,
  PermissionNotice,
  PermissionButton,
  usePermissions
} from '~/components/permissions';
import { USE_CASE_FAMILIES } from '~/components/home/use-case-catalog';
import {
  presetFileFieldConfigs,
  createEmptyPresetFileState,
  canSharePreset,
  combinePresetFiles,
  createPresetFormData,
  filterSelectablePresetIds,
  presetRowHref,
  presetTemplateAttachments,
  presetTemplateRefsForSubmit,
  presetNeedsReview,
  type PresetFileState
} from '~/components/presets/presets.utils';
import { PresetShareDialog } from '~/components/presets/preset-share-dialog';
import { LibraryEditOverlayLoader } from '~/components/library/library-edit-overlay-loader';
import {
  PresetRunOutputTemplatesField,
  usePresetRunOutputTemplates,
  type PresetRunOutputTemplatePickers
} from '~/components/presets/preset-run-output-templates';
import {
  CopilotCertificateReviewForm,
  CopilotContractReviewForm,
  CopilotCoverageComparisonForm,
  CopilotCoverageGapAnalysisForm,
  CopilotDocumentExplorationForm,
  CopilotGenerateQuoteProposalForm,
  CopilotGenerateTemplateForm,
  CopilotUnderwritingAnalysisForm,
  CopilotSOVAnalysisForm,
  CopilotClaimsReviewForm,
  CopilotCommercialClassificationForm,
  CopilotLossRunAnalysisForm,
  CopilotRiskMatchingForm,
  CopilotRiskReportForm,
  CopilotEBBillingAuditForm,
  CopilotEBClaimsReviewForm,
  CopilotEBEnrollmentMaterialsForm,
  CopilotEBCensusNormalizationForm,
  CopilotEBContributionAnalysisForm,
  CopilotEBDocumentReviewForm,
  CopilotEBQuoteAnalysisForm,
  CopilotCommissionProcessingForm,
  WorkspaceCreateEffortProvider,
  WorkspaceCreateEffortSelect,
  WorkspaceCreateVisibilityProvider,
  CopilotWorkspaceCreateOptions
} from '~/components/presets/copilot-launcher';
import {
  defaultEffortLevel,
  effortLevelLabel,
  FALLBACK_EFFORT_LEVEL,
  initialEffortLevel,
  resolveEffortLevel,
  useEffortLevelChoices
} from '~/components/presets/effort-level';
import { EffortLevelSelect } from '~/components/presets/effort-level-select';
import { ApolloError } from '@apollo/client';
import { getApplicationError, getValidationErrors } from '@oysterjs/core/graphql/errors';
import {
  type CopilotActionError,
  copilotActionErrorFromApplicationError
} from '~/lib/copilot-action-error';
import { CopilotSubmitError } from '~/components/overage-limit-error';
import { Await, TransitionDiv } from '@oysterjs/ui/async';
import { Button } from '@oysterjs/ui/button';
import { Badge } from '@oysterjs/ui/badge';
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogActions,
  MountableDialog
} from '@oysterjs/ui/dialog';
import { AutoResizeTextarea, Textarea } from '@oysterjs/ui/textarea';
import { FileInput, type FileInputChange, emptyFileInputChange } from '~/components/file';
import { type LaunchClient, LaunchClientContext } from '~/components/launch-client';
import { ValidationError } from '@oysterjs/core/graphql';
import { useFormActions } from '@oysterjs/core/graphql/form';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { Select } from '@oysterjs/ui/select';
import { BulkVisibilityDialog } from '~/components/visibility/bulk-visibility-dialog';

const CREATE_CUSTOM_PRESET = [{ resource: Resource.CustomPreset, action: Action.Create }];
const UPDATE_CUSTOM_PRESET = [{ resource: Resource.CustomPreset, action: Action.Update }];

export const PresetsPage = () => {
  const outlet = useOutlet();
  const location = useLocation();
  const submit = useSubmit();
  const navigation = useNavigation();
  const revalidator = useRevalidator();
  const agencyUser = useCurrentAgencyUser();
  const viewer = React.useMemo(
    () => ({ userId: agencyUser.id, teamIds: agencyUser.teams.map((t) => t.id) }),
    [agencyUser.id, agencyUser.teams]
  );
  const [selectedPresets, setSelectedPresets] = React.useState<string[]>([]);
  // The owned subset of the current selection, snapshotted when a bulk dialog opens.
  // Defense in depth: shared (read/run-only) presets must never enter bulk actions,
  // even though they are already excluded from the checkbox and "select all".
  const [bulkActionIds, setBulkActionIds] = React.useState<string[]>([]);
  const [showArchiveDialog, setShowArchiveDialog] = React.useState(false);
  const [showVisibilityDialog, setShowVisibilityDialog] = React.useState(false);
  const [isUpdatingVisibility, setIsUpdatingVisibility] = React.useState(false);
  const [visibilityError, setVisibilityError] = React.useState<string | null>(null);
  // The preset a share dialog was last opened for, snapshotted so the dialog
  // keeps its content through the close animation.
  const [sharePreset, setSharePreset] = React.useState<CustomPresetFieldsFragment | null>(null);
  const [showShareDialog, setShowShareDialog] = React.useState(false);
  const { hasPermission, hasAllPermissions } = usePermissions();
  // Sharing is admin-only: the grant/revoke mutations require CUSTOM_PRESET:SHARE.
  const hasSharePermission = hasPermission(Resource.CustomPreset, Action.Share);
  // Archiving and bulk visibility are both preset updates.
  const canUpdate = hasAllPermissions(UPDATE_CUSTOM_PRESET);
  // Running a preset creates a workspace, so the Run action follows that permission.
  const canRunPresets = hasAllPermissions(CREATE_CARA_WORKSPACE);
  const visibilityEnabled = useVisibilityEntitlement();
  const filterDefinitions = React.useMemo(() => createPresetFilterDefinitions(), []);
  const { activeFilters, setActiveFilters, initialFilters, sortBy, sortDirection, setSort } =
    useFiltersFromLocalStorage<PresetFilter, PresetSortKey>(
      'cara_presets_default_view',
      filterDefinitions,
      { defaultSortDirection: 'desc' }
    );
  const loader = useLoaderData() as
    | {
        data: Promise<{
          presets?: CustomPresetFieldsFragment[];
          federatedChildAgencies?: FederatedChildAgencyFieldsFragment[];
        }>;
      }
    | undefined;

  const archivePresets = (presetIds: string[]) => {
    submit(
      { presetIds },
      {
        method: 'POST',
        action: '/app/presets/archive',
        encType: 'application/json'
      }
    );
  };

  const isArchiving =
    navigation.state === 'submitting' && navigation.formAction?.endsWith('/archive');

  const updatePresetVisibility = async (visibility: VisibilityConfigInput) => {
    setIsUpdatingVisibility(true);
    setVisibilityError(null);
    try {
      await client.mutate({
        mutation: UpdateCustomPresetsDocument,
        variables: {
          presetIds: bulkActionIds,
          input: { visibility }
        }
      });
      setShowVisibilityDialog(false);
      setSelectedPresets([]);
      revalidator.revalidate();
    } catch (err) {
      // The backend rejects widening a preset past the audience of a template
      // it pins — surface its message inline so the user can act on it.
      const applicationError = err instanceof ApolloError ? getApplicationError(err) : undefined;
      setVisibilityError(
        applicationError?.message ?? 'Could not update visibility. Please try again.'
      );
    } finally {
      setIsUpdatingVisibility(false);
    }
  };

  React.useEffect(() => {
    if (navigation.state === 'loading' && navigation.formAction?.endsWith('/archive')) {
      setSelectedPresets([]);
    }
  }, [navigation.state, navigation.formAction]);

  return (
    <PageTitle title="Presets" description="Create your own templates for Copilot workspaces">
      <div className="mt-8 space-y-4">
        <div className="flex gap-4 items-start">
          <div className="flex-1">
            <SearchFilterInput
              filterDefinitions={filterDefinitions}
              onFiltersChange={setActiveFilters}
              initialFilters={initialFilters}
              placeholder="Filter by title, creator, or search..."
            />
          </div>
          <PermissionButton
            permissions={CREATE_CUSTOM_PRESET}
            whenDenied="hide"
            color="sky"
            href="/app/presets/new"
            className="flex-none"
          >
            Create Preset
          </PermissionButton>
        </div>

        <React.Suspense fallback={<PresetsPageLoader />}>
          <Await resolve={loader?.data}>
            {({ presets, federatedChildAgencies }) => {
              const activePresets = filterPresets(
                presets?.filter((preset) => !preset.description?.startsWith('[Archived] ')) ?? [],
                activeFilters,
                viewer
              );

              return (
                <div className="relative">
                  <AnimatePresence>
                    {selectedPresets.length > 0 && (
                      <motion.div
                        className="fixed left-1/2 lg:left-[calc(8rem+50vw)] bottom-12 shadow-xl border border-neutral-100 dark:border-neutral-700 rounded-full bg-white dark:bg-neutral-800 p-2 z-10 flex gap-2"
                        initial={{ opacity: 0, translateX: '-50%', translateY: 100, scale: 0.9 }}
                        animate={{ opacity: 1, translateX: '-50%', translateY: 0, scale: 1 }}
                        exit={{ opacity: 0, translateX: '-50%', translateY: 100, scale: 0.9 }}
                        transition={{ duration: 0.15, ease: 'easeInOut' }}
                      >
                        <Headless.Button
                          className={clsx(
                            'flex items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium flex-shrink-0',
                            'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300 dark:after:focus-within:ring-white/20',
                            'text-white bg-blue-600/80 border border-transparent hover:bg-blue-600/90'
                          )}
                          onClick={() => setSelectedPresets([])}
                        >
                          <div>{selectedPresets.length} selected</div>
                          <div>
                            <XMarkIcon className="size-3.5" />
                          </div>
                        </Headless.Button>
                        {visibilityEnabled && canUpdate && (
                          <Headless.Button
                            className={clsx(
                              'flex items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium flex-shrink-0',
                              'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300 dark:after:focus-within:ring-white/20',
                              !isUpdatingVisibility &&
                                'text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 border-dashed hover:bg-neutral-100/50 dark:hover:bg-white/10',
                              isUpdatingVisibility &&
                                'text-neutral-500 dark:text-neutral-400 bg-neutral-50/50 dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-700 border-dashed cursor-default'
                            )}
                            onClick={() => {
                              setBulkActionIds(
                                filterSelectablePresetIds(activePresets, selectedPresets)
                              );
                              setShowVisibilityDialog(true);
                            }}
                            disabled={isUpdatingVisibility}
                            title="Change visibility"
                          >
                            <EyeIcon className="size-3.5" />
                            Visibility
                            {isUpdatingVisibility && <Spinner size="size-3.5" color="neutral" />}
                          </Headless.Button>
                        )}
                        {canUpdate && (
                          <Headless.Button
                            className={clsx(
                              'flex items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium flex-shrink-0',
                              'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300 dark:after:focus-within:ring-white/20',
                              !isArchiving &&
                                'text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 border-dashed hover:bg-neutral-100/50 dark:hover:bg-white/10',
                              isArchiving &&
                                'text-neutral-500 dark:text-neutral-400 bg-neutral-50/50 dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-700 border-dashed cursor-default'
                            )}
                            onClick={() => {
                              setBulkActionIds(
                                filterSelectablePresetIds(activePresets, selectedPresets)
                              );
                              setShowArchiveDialog(true);
                            }}
                            disabled={isArchiving}
                          >
                            <TrashIcon className="size-3.5" />
                            Archive
                            {isArchiving && <Spinner size="size-3.5" color="neutral" />}
                          </Headless.Button>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                  {activePresets.length === 0 ? (
                    <div className="rounded-lg bg-white shadow px-4 sm:px-6 py-12 text-center dark:bg-neutral-800">
                      <CubeIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
                      <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
                        No presets found
                      </h3>
                      <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                        {activeFilters.length > 0
                          ? 'Try adjusting your search or filters.'
                          : 'No presets available.'}
                      </p>
                    </div>
                  ) : (
                    <div className="px-4 sm:px-6 rounded-lg bg-white shadow dark:bg-neutral-800">
                      <DataTable
                        data={activePresets}
                        columns={getPresetColumns({
                          selectedSet: new Set(selectedPresets),
                          setSelectedPresets,
                          filtered: activePresets,
                          canRun: canRunPresets,
                          canEdit: canUpdate,
                          canShare: (preset) =>
                            canSharePreset(
                              preset,
                              hasSharePermission,
                              federatedChildAgencies ?? []
                            ),
                          onShare: (preset) => {
                            setSharePreset(preset);
                            setShowShareDialog(true);
                          }
                        })}
                        keyExtractor={(preset) => preset.id}
                        rowHref={presetRowHref}
                        tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                        sortKey={sortBy}
                        sortDirection={sortDirection}
                        onSort={setSort}
                        sortFn={presetSortFn}
                        loading={navigation.state === 'loading'}
                      />
                    </div>
                  )}
                </div>
              );
            }}
          </Await>
        </React.Suspense>
      </div>

      <AnimatePresence mode="wait" initial>
        {outlet && React.cloneElement(outlet, { key: location.pathname })}
      </AnimatePresence>
      <Dialog open={showArchiveDialog} onClose={() => setShowArchiveDialog(false)} size="md">
        <DialogTitle>Archive presets</DialogTitle>
        <DialogDescription>
          Are you sure you want to archive <strong>{bulkActionIds.length}</strong>{' '}
          {bulkActionIds.length === 1 ? 'preset' : 'presets'}? They will be removed from the list
          and Cara will no longer be able to find or use them for new workspaces. Existing
          workspaces that reference them will still function.
        </DialogDescription>
        <DialogActions>
          <Button
            type="button"
            color="white"
            onClick={() => setShowArchiveDialog(false)}
            disabled={isArchiving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            color="sky"
            onClick={() => {
              archivePresets(bulkActionIds);
              setShowArchiveDialog(false);
            }}
            loading={isArchiving}
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>
      <BulkVisibilityDialog
        open={showVisibilityDialog}
        count={bulkActionIds.length}
        itemLabel="preset"
        notice="This changes only the selected presets. Existing workspaces created from them keep their current visibility."
        errorMessage={visibilityError}
        loading={isUpdatingVisibility}
        onClose={() => {
          setShowVisibilityDialog(false);
          setVisibilityError(null);
        }}
        onSave={updatePresetVisibility}
      />
      {sharePreset && (
        <PresetShareDialog
          open={showShareDialog}
          presetId={sharePreset.id}
          presetTitle={sharePreset.title}
          onClose={() => setShowShareDialog(false)}
        />
      )}
    </PageTitle>
  );
};

export const PresetsPageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
          Loading presets...
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          This might take a moment.
        </p>
      </div>
    </div>
  );
};

const tools = [
  {
    name: 'Web Research',
    description: 'Cara can search the web for specific information and use it to complete the task.'
  },
  {
    name: 'Draft Email',
    description: 'Cara can draft an email to a particular individual.'
  },
  {
    name: 'Generate Document',
    description: 'Cara can generate a PDF document based on the provided information.'
  },
  {
    name: 'Search File',
    description: 'Cara can search an uploaded file for information and use it to complete the task.'
  },
  {
    name: 'Search Knowledge Base',
    description:
      'Cara can search the knowledge base for information and use it to complete the task.'
  },
  {
    name: 'Search Knowledge Graph',
    description:
      'Cara can search integration data (known as the knowledge graph) for information and use it to complete the task.'
  },
  {
    name: 'Write to Integration',
    description:
      'Cara can create tasks, notes, and upload documents to an active integration (e.g. an AMS or CRM) as part of the task.'
  }
];

type TemplateLibrary = {
  word: TemplatePickerOption[];
  html: TemplatePickerOption[];
  forms: TemplatePickerOption[];
  excel: TemplatePickerOption[];
};

type TemplateLibraryLoaderData = {
  wordDocumentTemplates?: WordDocumentTemplateFieldsFragment[];
  htmlDocumentTemplates?: HtmlDocumentTemplateFieldsFragment[];
  customForms?: CustomFormListFieldsFragment[];
  excelSpreadsheetTemplates?: ExcelSpreadsheetTemplateFieldsFragment[];
};

export const buildTemplateLibrary = (data: TemplateLibraryLoaderData): TemplateLibrary => ({
  word: wordTemplatePickerOptions(data.wordDocumentTemplates || []),
  html: htmlTemplatePickerOptions(data.htmlDocumentTemplates || []),
  forms: customFormPickerOptions(data.customForms || []),
  excel: excelTemplatePickerOptions(data.excelSpreadsheetTemplates || [])
});

export const buildOutputTemplatePickers = (
  templateLibrary: TemplateLibrary
): PresetRunOutputTemplatePickers => ({
  customFormPicker: {
    options: templateLibrary.forms,
    description: presetCustomFormPickerDescription
  },
  wordTemplatePicker: {
    options: templateLibrary.word,
    description: presetWordTemplatePickerDescription
  },
  htmlTemplatePicker: {
    options: templateLibrary.html,
    description: presetHTMLTemplatePickerDescription
  },
  excelTemplatePicker: {
    options: templateLibrary.excel,
    description: presetExcelTemplatePickerDescription
  }
});

export const PresetCreatePage = () => {
  const submit = useSubmit();
  const action = useActionData() as ActionResult<CreateCustomPresetMutation>;
  const loader = useLoaderData() as { data: Promise<TemplateLibraryLoaderData> } | undefined;
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  const submitForm = (form: CustomPresetInput) => {
    const formData = createPresetFormData(form);
    submit(formData, { method: 'POST', encType: 'multipart/form-data' });
  };

  return (
    <React.Suspense fallback={<LibraryEditOverlayLoader />}>
      <Await resolve={loader?.data}>
        {(data: TemplateLibraryLoaderData | undefined) => (
          <PresetsCreateOrEditPage
            editMode={false}
            initialForm={{
              title: '',
              fileIds: [],
              basePrompt: '',
              allowFileUpload: true,
              description: '',
              inheritFromPreset: null,
              defaultEffortLevel: null
            }}
            onSubmit={submitForm}
            saveError={action?.applicationError?.message}
            templateLibrary={buildTemplateLibrary(data ?? {})}
            validationErrors={validationErrors}
          />
        )}
      </Await>
    </React.Suspense>
  );
};

export const PresetEditPage = () => {
  const loader = useLoaderData() as
    | { data: Promise<{ preset?: CustomPresetDetailFieldsFragment } & TemplateLibraryLoaderData> }
    | undefined;

  return (
    <React.Suspense fallback={<LibraryEditOverlayLoader />}>
      <Await resolve={loader?.data}>
        {(data) => {
          if (!data?.preset) {
            return null;
          }

          // A shared preset is readable, so this editor stays reachable by direct URL.
          // Saving would fail @authorize, which the house action helper turns into a
          // /signin redirect — signing the user out and discarding their edits.
          if (!data.preset.isOwner) {
            return <Navigate to={`/app/presets/${data.preset.id}/run`} replace />;
          }

          const submit = useSubmit();
          const action = useActionData() as ActionResult<UpdateCustomPresetMutation>;
          const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);

          const submitForm = (form: CustomPresetInput) => {
            const formData = createPresetFormData(form);
            submit(formData, { method: 'PUT', encType: 'multipart/form-data' });
          };

          React.useEffect(() => {
            if (action?.validationErrors) {
              setValidationErrors(action.validationErrors);
            }
          }, [action?.validationErrors]);

          return (
            <PresetsCreateOrEditPage
              editMode
              needsReview={presetNeedsReview(data.preset)}
              initialForm={{
                title: data.preset.title || '',
                basePrompt: data.preset.basePrompt || '',
                allowFileUpload: data.preset.allowFileUpload,
                description: data.preset.description || '',
                inheritFromPreset: data.preset.inheritFromPreset || null,
                defaultEffortLevel: data.preset.defaultEffortLevel ?? null
              }}
              initialPreset={data.preset}
              onSubmit={submitForm}
              saveError={action?.applicationError?.message}
              templateLibrary={buildTemplateLibrary(data)}
              validationErrors={validationErrors}
            />
          );
        }}
      </Await>
    </React.Suspense>
  );
};

type PresetFile = NonNullable<CustomPresetDetailFieldsFragment['files']>[number];

/**
 * The files already saved on the preset, each with the link that drops it.
 * Rendered under both file-field branches: a built-in base preset brings its own
 * upload buckets, but the files saved on the custom preset are still permanent
 * context the owner has to be able to remove.
 */
const PresetSavedFiles = (props: { files: PresetFile[]; onRemove: (fileId: string) => void }) => {
  if (props.files.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 mt-4">
      {props.files.map((file) => (
        <div key={file.id} className="flex flex-row gap-2">
          <DocumentIcon className="size-10 text-neutral-400 dark:text-neutral-500" />
          <div className="flex flex-col">
            <div className="text-sm text-neutral-700 dark:text-neutral-300 truncate text-ellipsis">
              {file.name}
            </div>
            <a
              onClick={(e) => {
                e.preventDefault();
                props.onRemove(file.id);
              }}
              className="text-sm text-primary-500 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-200 hover:underline hover:cursor-pointer"
            >
              Remove File
            </a>
          </div>
        </div>
      ))}
    </div>
  );
};

export const PresetsCreateOrEditPage = (props: {
  editMode?: boolean;
  initialForm: CustomPresetInput;
  initialPreset?: CustomPresetDetailFieldsFragment;
  onSubmit: (form: CustomPresetInput) => void;
  saveError?: string;
  templateLibrary: TemplateLibrary;
  validationErrors: ValidationError[];
  onClose?: () => void;
  submitting?: boolean;
  errorMessage?: string | null;
  /**
   * An AI-generated preset the user has not accepted yet. Saving it promotes the
   * preset to READY, so the editor presents the pass as a review rather than an
   * ordinary edit.
   */
  needsReview?: boolean;
}) => {
  // Reachable by URL on both routes, so the editor checks the permission that
  // route needs rather than trusting the button that normally opens it.
  const canSave = usePermissions().hasAllPermissions(
    props.editMode ? UPDATE_CUSTOM_PRESET : CREATE_CUSTOM_PRESET
  );
  const navigation = useNavigation();
  const navigate = useNavigate();
  const closeEditor = props.onClose ?? (() => navigate(-1));
  const isSubmitting = props.submitting ?? navigation.state === 'submitting';

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>(
    props.validationErrors
  );
  const [form, setForm] = React.useState<CustomPresetInput>(props.initialForm);
  const [existingFileIds, setExistingFileIds] = React.useState<string[]>(
    props.initialPreset?.files?.map((f) => f.id) || []
  );
  const savedFiles = React.useMemo(
    () => props.initialPreset?.files?.filter((f) => existingFileIds.includes(f.id)) ?? [],
    [props.initialPreset?.files, existingFileIds]
  );
  const removeSavedFile = (fileId: string) =>
    setExistingFileIds((prev) => prev.filter((p) => p !== fileId));
  const visibilityEntitled = useVisibilityEntitlement();
  const agencyUser = useCurrentAgencyUser();
  const disallowPrivate = agencyUser.agency.configuration.visibility.disallowPrivate;
  const editingOwnerCheck = useVisibilityEditAllowed(props.initialPreset?.createdBy?.id);
  // createdBy is null for a cross-agency grantee, which lands in
  // useVisibilityEditAllowed's "unknown owner means allow" branch, so require
  // ownership explicitly.
  const editingAllowed = editingOwnerCheck && (props.initialPreset?.isOwner ?? true);
  const visibilityEnabled = props.editMode ? editingAllowed : visibilityEntitled;
  const defaultVisibility = useDefaultVisibilityValue();
  const [visibility, setVisibility] = React.useState<VisibilitySelectorValue>(
    props.initialPreset
      ? defaultVisibilityValueFromFragment(props.initialPreset.visibility, { disallowPrivate })
      : defaultVisibility
  );
  // Consolidated file state for different preset types
  const [fileState, setFileState] = React.useState<PresetFileState>(createEmptyPresetFileState());
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  const templateAttachments = useCaraAttachments();
  // Gates the picker, mirroring the "Create Branded Document" toggle. Off means
  // the preset pins no output templates; starts on only when it already has some.
  const [useOutputTemplates, setUseOutputTemplates] = React.useState(
    () => presetTemplateAttachments(props.initialPreset).length > 0
  );

  React.useEffect(() => {
    if (props.validationErrors?.length) {
      setValidationErrors(props.validationErrors);
    }
  }, [props.validationErrors]);

  React.useEffect(() => {
    setExistingFileIds(props.initialPreset?.files?.map((f) => f.id) || []);
    setFileState(createEmptyPresetFileState());
    const initialTemplates = presetTemplateAttachments(props.initialPreset);
    templateAttachments.clear();
    templateAttachments.add(...initialTemplates);
    setUseOutputTemplates(initialTemplates.length > 0);
  }, [props.initialPreset?.id]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const allUploadFiles = combinePresetFiles(fileState, form.inheritFromPreset ?? null);

    props.onSubmit({
      ...form,
      fileIds: existingFileIds.length > 0 ? existingFileIds : null,
      uploadFiles:
        allUploadFiles.length > 0
          ? allUploadFiles.filter((f) => 'upload' in f && f.upload).map((f) => f.upload!)
          : null,
      visibility: visibilityEnabled ? visibilityInputFromValue(visibility) : undefined,
      ...presetTemplateRefsForSubmit(useOutputTemplates, templateAttachments.attachments)
    });
  };

  const [showHelp, setShowHelp] = React.useState(false);
  const { enabled: effortSelectorEnabled, choices: defaultEffortChoices } = useEffortLevelChoices();
  // A saved Auto the selector no longer offers shows as the level it runs at,
  // and stays saved unless another level is picked.
  const presetEffortLevel: CaraWorkspaceEffortLevel =
    defaultEffortChoices.find((choice) => choice.level === form.defaultEffortLevel)?.level ??
    defaultEffortLevel(defaultEffortChoices);

  const inheritFromPresetOptions = USE_CASE_FAMILIES;

  return (
    <MountableDialog onClose={closeEditor} size={showHelp ? '5xl' : '2xl'} className="relative">
      <Headless.Button
        onClick={() => setShowHelp((prev) => !prev)}
        className={clsx(
          // top-24 (96px) keeps this clear of the dialog's top-right cluster
          // (visibility control at top of the title row) on the compose form.
          'z-50 absolute top-24 text-neutral-600 dark:text-neutral-400 bg-neutral-200 dark:bg-neutral-700 hover:bg-neutral-600 hover:text-neutral-100 transition-all duration-150 flex flex-row gap-1 items-center rounded-full py-1 px-2',
          showHelp ? 'right-12' : 'right-8'
        )}
      >
        {showHelp ? (
          <ChevronDoubleRightIcon className="size-3" />
        ) : (
          <ChevronDoubleLeftIcon className="size-3" />
        )}
        {showHelp ? (
          <div className="text-xs">Hide help</div>
        ) : (
          <div className="text-xs">Show help</div>
        )}
      </Headless.Button>
      <div className="flex flex-row gap-8">
        <div className="max-w-[608px]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <DialogTitle>
                <span className="flex items-center gap-2">
                  {props.needsReview
                    ? 'Review preset'
                    : props.editMode
                      ? 'Edit preset'
                      : 'Create a new preset'}
                  {props.needsReview && <Badge color="amber">Needs review</Badge>}
                </span>
              </DialogTitle>
              <DialogDescription>
                {props.editMode
                  ? 'Update the preset settings and save your changes.'
                  : 'Define a new preset and start creating copilot workspaces with it.'}
              </DialogDescription>
            </div>
          </div>
          <Form onSubmit={handleSubmit}>
            <DialogBody>
              {!canSave && (
                <PermissionNotice className="mb-6">
                  You can view this preset but not change it.
                </PermissionNotice>
              )}
              <Fieldset disabled={!canSave}>
                <FieldGroup>
                  <Field>
                    <Label>Title</Label>
                    <Description>
                      A brief, descriptive title for the preset. This should be two to four words.
                    </Description>
                    <Input
                      className="max-w-xs"
                      value={form?.title}
                      onChange={(e) => {
                        const title = e.currentTarget.value;
                        updateForm(['title'], (prev) => ({ ...prev, title }));
                      }}
                      invalid={!!getValidationError(validationErrors, ['title'])}
                    />
                    {!!getValidationError(validationErrors, ['title']) && (
                      <ErrorMessage>{getValidationError(validationErrors, ['title'])}</ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Description</Label>
                    <Description>
                      A brief description of the preset does. This should be about one sentence.
                    </Description>
                    <Textarea
                      value={form?.description}
                      rows={2}
                      resizable={false}
                      onChange={(e) => {
                        const description = e.currentTarget.value;
                        updateForm(['description'], (prev) => ({ ...prev, description }));
                      }}
                      invalid={!!getValidationError(validationErrors, ['description'])}
                    />
                    {!!getValidationError(validationErrors, ['description']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['description'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Base Preset</Label>
                    <Description>
                      The built-in preset to inherit from. This will use the inherited preset's
                      prompt and performance as baseline and layer this preset's base prompt on top.
                    </Description>
                    <Select
                      value={form?.inheritFromPreset || ''}
                      onChange={(e) => {
                        const inheritFromPreset = e.currentTarget.value;
                        updateForm(['inheritFromPreset'], (prev) => ({
                          ...prev,
                          inheritFromPreset: inheritFromPreset
                            ? (inheritFromPreset as CaraWorkspacePreset)
                            : null
                        }));
                        // Reset all file states when changing preset
                        setFileState(createEmptyPresetFileState());
                      }}
                      invalid={!!getValidationError(validationErrors, ['inheritFromPreset'])}
                    >
                      <option value="">None</option>
                      {inheritFromPresetOptions.map((option) => (
                        <option key={option.preset} value={option.preset}>
                          {option.title}
                        </option>
                      ))}
                    </Select>
                    {!!getValidationError(validationErrors, ['inheritFromPreset']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['inheritFromPreset'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  {effortSelectorEnabled && (
                    <Field>
                      <Label>Default Analysis Depth</Label>
                      <Description>
                        The analysis depth workspaces from this preset start at. Whoever runs it can
                        still change it before starting.
                        {presetEffortLevel === CaraWorkspaceEffortLevel.High &&
                          ` Runs at ${effortLevelLabel(FALLBACK_EFFORT_LEVEL)} for anyone not on a plan with usage-based pricing.`}
                      </Description>
                      <div data-slot="control">
                        <EffortLevelSelect
                          value={presetEffortLevel}
                          onChange={(level) =>
                            updateForm(['defaultEffortLevel'], (prev) => ({
                              ...prev,
                              defaultEffortLevel: level
                            }))
                          }
                          choices={defaultEffortChoices}
                          anchor="bottom start"
                          inField
                        />
                      </div>
                      {!!getValidationError(validationErrors, ['defaultEffortLevel']) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, ['defaultEffortLevel'])}
                        </ErrorMessage>
                      )}
                    </Field>
                  )}
                  <Field>
                    <Label>Base Prompt</Label>
                    <Description>
                      Specify detailed instructions for what you want Cara to do. Read the{' '}
                      <a
                        onClick={() => setShowHelp(true)}
                        className="text-sky-600 dark:text-sky-400 hover:text-sky-900 dark:hover:text-sky-200 hover:underline cursor-pointer"
                      >
                        help
                      </a>{' '}
                      section for tips and guidelines on how to prompt Cara.
                    </Description>
                    <AutoResizeTextarea
                      className="font-mono"
                      value={form?.basePrompt}
                      onChange={(e) => {
                        const basePrompt = e.currentTarget.value;
                        updateForm(['basePrompt'], (prev) => ({ ...prev, basePrompt }));
                      }}
                      invalid={!!getValidationError(validationErrors, ['basePrompt'])}
                    />
                    {!!getValidationError(validationErrors, ['basePrompt']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['basePrompt'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  {form.inheritFromPreset && presetFileFieldConfigs[form.inheritFromPreset] ? (
                    <>
                      {presetFileFieldConfigs[form.inheritFromPreset].map((fieldConfig) => (
                        <Field key={fieldConfig.key}>
                          <Label>{fieldConfig.label}</Label>
                          <Description>{fieldConfig.description}</Description>
                          <FileInput
                            multiple
                            onChange={(change) =>
                              setFileState((prev) => ({
                                ...prev,
                                [fieldConfig.key]: change.files
                              }))
                            }
                          />
                        </Field>
                      ))}
                      {/* The base preset's buckets name the role a file plays in a
                          run, and a file already saved carries no such label, so it
                          gets a field of its own rather than being sorted into one. */}
                      {savedFiles.length > 0 && (
                        <Field>
                          <Label>Saved Files</Label>
                          <Description>
                            Documents already saved on this preset. They are included in every
                            workspace created from it.
                          </Description>
                          <div data-slot="control">
                            <PresetSavedFiles files={savedFiles} onRemove={removeSavedFile} />
                          </div>
                        </Field>
                      )}
                    </>
                  ) : (
                    <Field>
                      <Label>Include Files</Label>
                      <Description>
                        Upload any documents to automatically include in workspaces created from
                        this preset.
                      </Description>
                      <div data-slot="control">
                        <FileInput
                          multiple
                          onChange={(change) =>
                            setFileState((prev) => ({ ...prev, upload: change.files }))
                          }
                        />
                        <PresetSavedFiles files={savedFiles} onRemove={removeSavedFile} />
                      </div>
                    </Field>
                  )}
                  <SwitchField>
                    <Label>Fill Templates or Forms</Label>
                    <Description>
                      Specify any templates or forms from the Library that Cara should fill when
                      generating deliverables from this preset. Turning this off clears them on
                      save.
                    </Description>
                    <Switch
                      color="sky"
                      checked={useOutputTemplates}
                      onChange={(v) => setUseOutputTemplates(v)}
                    />
                  </SwitchField>
                  {useOutputTemplates && (
                    <Field>
                      <OutputTemplatePicker
                        attachments={templateAttachments.attachments}
                        onAdd={templateAttachments.add}
                        onRemove={templateAttachments.remove}
                        buttonLabel="Add custom template or form"
                        buttonAriaLabel="Add custom template or form"
                        customFormPicker={{
                          options: props.templateLibrary.forms,
                          description: presetCustomFormPickerDescription
                        }}
                        wordTemplatePicker={{
                          options: props.templateLibrary.word,
                          description: presetWordTemplatePickerDescription
                        }}
                        htmlTemplatePicker={{
                          options: props.templateLibrary.html,
                          description: presetHTMLTemplatePickerDescription
                        }}
                      />
                    </Field>
                  )}
                </FieldGroup>
              </Fieldset>
              {props.errorMessage && (
                <ErrorMessage className="mt-4">{props.errorMessage}</ErrorMessage>
              )}
              {props.saveError && <ErrorMessage className="mt-4">{props.saveError}</ErrorMessage>}
            </DialogBody>
            <DialogActions>
              <Button type="button" color="white" disabled={isSubmitting} onClick={closeEditor}>
                Close
              </Button>
              {!canSave ? null : !visibilityEnabled ? (
                <Button type="submit" color="sky" disabled={!form} loading={isSubmitting}>
                  {props.needsReview
                    ? 'Approve preset'
                    : props.editMode
                      ? 'Update Preset'
                      : 'Create Preset'}
                </Button>
              ) : (
                <VisibilitySplitButton
                  type="submit"
                  value={visibility}
                  onChange={setVisibility}
                  disabled={!form}
                  loading={isSubmitting}
                  matchDisabledAppearance={!form}
                >
                  {props.needsReview
                    ? 'Approve preset'
                    : props.editMode
                      ? 'Update Preset'
                      : 'Create Preset'}
                </VisibilitySplitButton>
              )}
            </DialogActions>
          </Form>
        </div>
        {showHelp && (
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: '320px' }}
            transition={{ delay: 0.15, duration: 0.15, ease: 'easeOut' }}
            className="flex-none bg-neutral-50 dark:bg-neutral-900 rounded-lg p-6 overflow-y-auto max-h-[733.5px]"
          >
            <motion.div
              initial={{ opacity: 0, display: 'none' }}
              animate={{ opacity: 1, display: 'block' }}
              transition={{ delay: 0.3, duration: 0.15 }}
            >
              <h2 className="text-lg font-medium text-neutral-900 dark:text-white mt-0 pt-0">
                Help
              </h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Cara is an insurance-specific AI that you can program to perform custom tasks.
                Here's an overview of how to effectively use Cara.
              </p>
              <h3 className="text-sm font-medium text-neutral-900 dark:text-white mt-4">
                Prompting Cara
              </h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Cara's performance depends on the quality of your prompt. You'll get the best
                results if you follow these guidelines:
                <ul className="mt-3 -ml-7">
                  <li className="mt-1.5">
                    <strong className="text-neutral-900 dark:text-white font-medium">
                      Treat it like a human.
                    </strong>{' '}
                    Cara is smart, but does not know your specific use case. Train it like you would
                    a new employee.
                  </li>
                  <li className="mt-1.5">
                    <strong className="text-neutral-900 dark:text-white font-medium">
                      Be detailed and specific.
                    </strong>{' '}
                    Cara is capable, but not a mind-reader. Tell it exactly what you want it to do.
                  </li>
                  <li className="mt-1.5">
                    <strong className="text-neutral-900 dark:text-white font-medium">
                      Provide context.
                    </strong>{' '}
                    Include context, background, examples, and any key facts and information that
                    can help Cara reason about the task.
                  </li>
                  <li className="mt-1.5">
                    <strong className="text-neutral-900 dark:text-white font-medium">
                      Specify the output format.
                    </strong>{' '}
                    If you want Cara to respond in a particular format, instruct her explicitly.
                  </li>
                </ul>
              </p>
              <h3 className="text-sm font-medium text-neutral-900 dark:text-white mt-4">Tools</h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Cara has access to a variety of tools that can help it complete your request. You
                can tell it explicitly to use these or instruct it on how to decide whether to use
                them.
              </p>
              <ul className="mt-3">
                {tools.map((tool) => (
                  <li className="mt-1.5" key={tool.name}>
                    <strong className="text-sm text-neutral-900 dark:text-white font-medium">
                      {tool.name}
                    </strong>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      {tool.description}
                    </p>
                  </li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        )}
      </div>
    </MountableDialog>
  );
};

/**
 * `client` files the new workspace under that client, for a preset run from its
 * page. `library` is for a parent route that has already loaded the templates.
 */
export const PresetRunPage = (props: {
  client?: LaunchClient;
  library?: TemplateLibraryLoaderData;
}) => {
  const loader = useLoaderData() as
    | {
        data: Promise<{ preset?: CustomPresetDetailFieldsFragment } & TemplateLibraryLoaderData>;
      }
    | undefined;

  return (
    <React.Suspense
      fallback={
        <div className="fixed z-50 top-0 left-0 w-full h-full flex items-center justify-center">
          <TransitionDiv className="my-16 flex items-center justify-center">
            <div className="rounded-lg bg-white dark:bg-neutral-800 p-10 shadow">
              <Spinner size="size-10" color="neutral" />
            </div>
          </TransitionDiv>
        </div>
      }
    >
      <Await resolve={loader?.data}>
        {(data) =>
          data?.preset && (
            <LaunchClientContext.Provider value={props.client ?? null}>
              <PresetRunPageDialog
                preset={data.preset}
                templateLibrary={buildTemplateLibrary(props.library ?? data)}
                client={props.client}
              />
            </LaunchClientContext.Provider>
          )
        }
      </Await>
    </React.Suspense>
  );
};

const PresetRunPageDialog = (props: {
  preset: CustomPresetDetailFieldsFragment;
  templateLibrary: TemplateLibrary;
  client?: LaunchClient;
}) => {
  const navigate = useNavigate();
  const outputTemplatePickers = React.useMemo(
    () => buildOutputTemplatePickers(props.templateLibrary),
    [props.templateLibrary]
  );
  const initialOutputTemplates = React.useMemo(
    () => presetTemplateAttachments(props.preset),
    [props.preset]
  );
  const outputTemplates = usePresetRunOutputTemplates(initialOutputTemplates);
  const outputTemplatesField = (
    <PresetRunOutputTemplatesField
      useOutputTemplates={outputTemplates.useOutputTemplates}
      onUseOutputTemplatesChange={outputTemplates.setUseOutputTemplates}
      attachments={outputTemplates.templateAttachments.attachments}
      onAdd={outputTemplates.templateAttachments.add}
      onRemove={outputTemplates.templateAttachments.remove}
      outputTemplatePickers={outputTemplatePickers}
    />
  );

  // Running a preset creates a workspace, so that is the permission it needs --
  // not anything on the preset itself.
  const canRun = usePermissions().hasAllPermissions(CREATE_CARA_WORKSPACE);
  // Use a ref to prevent race conditions when creating workspaces (e.g., double-clicking submit).
  // Unlike useState, ref updates are synchronous - when we set loadingRef.current = true,
  // the value is immediately available on the next line. With useState alone, setLoading(true)
  // schedules an update for the next render, so multiple rapid calls would all see
  // loading=false and execute concurrently, potentially creating duplicate workspaces.
  // We keep the loading state for UI purposes (disabling buttons, showing spinners).
  const loadingRef = React.useRef(false);
  const [error, setError] = React.useState<CopilotActionError | undefined>();
  const [message, setMessage] = React.useState<string>('');
  const [attachments, setAttachments] = React.useState<FileInputChange>(emptyFileInputChange());
  const visibilityEnabled = useVisibilityEntitlement();
  const defaultVisibility = useDefaultVisibilityValue();
  const [visibility, setVisibility] = React.useState<VisibilitySelectorValue>(defaultVisibility);
  // No preset here: a custom preset is not narrowed the way the built-in
  // comparison is, so it is offered every level the selector is.
  const { enabled: effortEnabled, choices: effortChoices } = useEffortLevelChoices();
  const [effortLevel, setEffortLevel] = React.useState<CaraWorkspaceEffortLevel>(() =>
    initialEffortLevel(props.preset.defaultEffortLevel, effortChoices)
  );

  // Wrap the copilot form's onSubmit to inject customPresetId
  const createWorkspace = async (
    query: string,
    files: CaraWorkspaceFileInput[],
    options?: CopilotWorkspaceCreateOptions
  ) => {
    if (loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    setError(undefined);
    try {
      const outputTemplateOptions = outputTemplates.getWorkspaceOptions();
      // Separate uploads from existing file references
      const uploadFiles = files.filter((f) => 'upload' in f && f.upload).map((f) => f.upload!);
      const existingFiles = files
        .filter((f) => 'existing' in f && f.existing)
        .map((f) => f.existing!);

      const res = await client.mutate<CreateCaraWorkspaceMutation>({
        mutation: CreateCaraWorkspaceDocument,
        variables: {
          input: {
            message: query.trim(),
            files: uploadFiles,
            existingFiles: existingFiles.length > 0 ? existingFiles : undefined,
            customPresetId: props.preset.id,
            documentIds: options?.documentIds || [],
            presetMetadata: options?.presetMetadata,
            visibility: visibilityEnabled ? visibilityInputFromValue(visibility) : undefined,
            amsContext: options?.amsContext?.length ? options.amsContext : undefined,
            amsRefs: options?.amsRefs?.length ? options.amsRefs : undefined,
            clientId: props.client?.id,
            // Left unset without the selector, so the server applies the
            // preset's default under its own rules.
            effortLevel:
              options?.effortLevel ??
              (effortEnabled ? resolveEffortLevel(effortLevel, effortChoices) : undefined),
            ...outputTemplateOptions
          }
        }
      });

      const applicationError = getApplicationError(new ApolloError({ graphQLErrors: res.errors }));
      const validationErrors = getValidationErrors(new ApolloError({ graphQLErrors: res.errors }));

      if (applicationError) {
        setError(copilotActionErrorFromApplicationError(applicationError));
      } else if (validationErrors?.length) {
        setError({ kind: 'generic', message: validationErrors[0].message });
      } else if (res.errors?.length) {
        setError({ kind: 'generic', message: res.errors[0].message });
      } else if (res.data?.createCaraWorkspace?.id) {
        navigate(`/app/workspaces/${res.data.createCaraWorkspace.id}`);
      } else {
        setError({ kind: 'generic', message: 'Failed to create workspace' });
      }
    } catch (e) {
      // Log the error and set it for display
      // eslint-disable-next-line no-console
      console.error(e);
      if (e instanceof ApolloError) {
        const applicationError = getApplicationError(e);
        const validationErrors = getValidationErrors(e);
        if (applicationError) {
          setError(copilotActionErrorFromApplicationError(applicationError));
        } else {
          setError({
            kind: 'generic',
            message: validationErrors?.[0]?.message ?? e.message ?? 'Failed to create workspace'
          });
        }
      } else {
        setError({ kind: 'generic', message: 'Failed to create workspace' });
      }
    } finally {
      loadingRef.current = false;
    }
  };

  const sharedPresetRunFormProps = {
    onSubmit: createWorkspace,
    error,
    onClose: () => navigate(-1),
    outputTemplatesField
  };

  // Show preset files if they exist, then render the appropriate copilot form
  const renderPresetFiles = () => {
    if (!props.preset.files?.length) return null;
    return (
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Preset Files (Already Included)</Label>
              <Description>
                These files are automatically included from the preset and will be used as context.
              </Description>
              <div className="flex flex-col gap-2 mt-2">
                {props.preset.files.map((file) => (
                  <div key={file.id} className="flex flex-row gap-2 items-center">
                    <DocumentIcon className="size-5 text-neutral-400 dark:text-neutral-500" />
                    <div className="text-sm text-neutral-700 dark:text-neutral-300">
                      {file.name}
                    </div>
                  </div>
                ))}
              </div>
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
    );
  };

  if (!canRun) {
    return (
      <MountableDialog onClose={() => navigate(-1)} size="xl">
        <DialogTitle>{props.preset.title}</DialogTitle>
        <DialogDescription>{props.preset.description}</DialogDescription>
        <PermissionNotice className="mt-6">
          You don&rsquo;t have permission to start workspaces, so this preset cannot be run. Ask an
          agency admin for access.
        </PermissionNotice>
      </MountableDialog>
    );
  }

  return (
    <MountableDialog onClose={() => navigate(-1)} size="xl">
      <DialogTitle>{props.preset.title}</DialogTitle>
      <DialogDescription>{props.preset.description}</DialogDescription>
      {renderPresetFiles()}
      <WorkspaceCreateVisibilityProvider
        value={visibilityEnabled ? { value: visibility, onChange: setVisibility } : null}
      >
        <WorkspaceCreateEffortProvider
          value={effortEnabled ? { value: effortLevel, onChange: setEffortLevel } : null}
        >
          {props.preset.inheritFromPreset === CaraWorkspacePreset.CertificateReview ? (
            <CopilotCertificateReviewForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.ContractReview ? (
            <CopilotContractReviewForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.CoverageComparison ? (
            <CopilotCoverageComparisonForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.GenerateQuoteProposal ? (
            <CopilotGenerateQuoteProposalForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.DocumentExploration ? (
            <CopilotDocumentExplorationForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.UnderwritingAnalysis ? (
            <CopilotUnderwritingAnalysisForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.CommercialClassification ? (
            <CopilotCommercialClassificationForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.GenerateTemplate ? (
            <CopilotGenerateTemplateForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.SovAnalysis ? (
            <CopilotSOVAnalysisForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.ClaimsReview ? (
            <CopilotClaimsReviewForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.CoverageGapAnalysis ? (
            <CopilotCoverageGapAnalysisForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.LossRunAnalysis ? (
            <CopilotLossRunAnalysisForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.RiskMatching ? (
            <CopilotRiskMatchingForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.RiskReport ? (
            <CopilotRiskReportForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.EbContributionAnalysis ? (
            <CopilotEBContributionAnalysisForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.EbDocumentReview ? (
            <CopilotEBDocumentReviewForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.EbCensusNormalization ? (
            // No saved-layout picker on this route: this dialog's loader does not
            // fetch the Excel template library. The blank-workbook upload covers a
            // custom preset run, and the form omits the picker rather than showing
            // an empty one.
            <CopilotEBCensusNormalizationForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.EbQuoteAnalysis ? (
            <CopilotEBQuoteAnalysisForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.EbBillingAudit ? (
            <CopilotEBBillingAuditForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.EbClaimsReview ? (
            <CopilotEBClaimsReviewForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.EbEnrollmentMaterials ? (
            <CopilotEBEnrollmentMaterialsForm {...sharedPresetRunFormProps} />
          ) : props.preset.inheritFromPreset === CaraWorkspacePreset.CommissionProcessing ? (
            // No saved-layout picker here either, for the same reason as census
            // normalization: this dialog's loader does not fetch the Excel
            // template library. Uploading a blank workbook still reaches the same
            // destination-layout path.
            <CopilotCommissionProcessingForm {...sharedPresetRunFormProps} />
          ) : (
            // Fallback for presets without inheritFromPreset - use a simple form
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                createWorkspace(message, attachments.files, {
                  amsContext:
                    attachments.amsContext.length > 0 ? attachments.amsContext : undefined,
                  amsRefs: attachments.amsRefs?.length ? attachments.amsRefs : undefined
                });
              }}
            >
              <DialogBody>
                <Fieldset>
                  <FieldGroup>
                    <Field>
                      <Label>Input Files</Label>
                      <Description>
                        Files you'd like Cara to use as context to answer questions
                      </Description>
                      <FileInput
                        multiple
                        enableExisting
                        enableAMSSearch
                        onChange={(change) => setAttachments(change)}
                      />
                    </Field>
                    <Field>
                      <Label>Message</Label>
                      <Description>
                        Any additional instructions or context you'd like Cara to use to complete
                        the task.
                      </Description>
                      <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.currentTarget.value)}
                        rows={5}
                      />
                    </Field>
                    {outputTemplatesField}
                  </FieldGroup>
                </Fieldset>
                {error && <CopilotSubmitError className="mt-4" error={error} />}
              </DialogBody>
              <DialogActions>
                <Button type="button" color="white" onClick={() => navigate(-1)}>
                  Close
                </Button>
                <WorkspaceCreateEffortSelect />
                {visibilityEnabled ? (
                  <VisibilitySplitButton type="submit" value={visibility} onChange={setVisibility}>
                    Create Workspace
                  </VisibilitySplitButton>
                ) : (
                  <Button type="submit" color="sky">
                    Create Workspace
                  </Button>
                )}
              </DialogActions>
            </Form>
          )}
        </WorkspaceCreateEffortProvider>
      </WorkspaceCreateVisibilityProvider>
    </MountableDialog>
  );
};
